import { Ref, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'

import { ValidateSchemaPersonal } from 'pages/clientsCreate/model/validateSchemaPersonal'
import { ValidateSchemaOrg } from 'pages/clientsCreate/model/validateSchemaOrg'
import { useParseCounterparty } from 'features/Clients/model/useParseCounterparty'
import { PersonalProfileForm } from 'features/Clients/ui/components/PersonalProfileForm'
import { CompanyProfileForm } from 'features/Clients/ui/components/CompanyProfileForm'
import { FormKeys, useFormConfig } from 'features/Clients/model/useFormParams'
import { useGetCounterpartyByIdQuery } from 'entities/counterparties/model'
import { Loader } from 'shared/components/Loader'

import {
    getInitialValuesCompany,
    getInitialValuesPersonal,
} from '../model/getInitialValues'
import { getDynamicCreationReducerHook } from 'pages/clientsCreate/model/dynamicCreationReducers'
import { ContactDTO } from 'entities/counterparties/model/dtoTypes'

interface IPropsProfile {
    isEditable: boolean
    formikRef: Ref<FormikHelpers<any>>
    setIsEditable: (e: boolean) => void
    setEditButtonText: (e: string) => void
    setDisabled: (e: boolean) => void
    setIsBlackList: (e: boolean | undefined | null) => void
}

export const ClientsProfile = ({
    isEditable,
    setIsEditable,
    formikRef,
    setEditButtonText,
    setDisabled,
    setIsBlackList,
}: IPropsProfile) => {
    const { id } = useParams()
    const location = useLocation()

    const currentCounterpartyId = id ? Number(id) : null

    const { counterparty, isLoading, refetch } = useGetCounterpartyByIdQuery(
        currentCounterpartyId,
    )
    const { isCompany } = useParseCounterparty(counterparty)

    const useDynamicCreationHandler = getDynamicCreationReducerHook(
        location.state?.dynamicCreationReducerType,
    )
    const dynamicCreationHandler = useDynamicCreationHandler()
    const locationInitCompanyStateProps =
        location.state?.initCompanyFormData ?? {}
    const [defaultContacts, setDefaultContacts] = useState<ContactDTO[]>(
        locationInitCompanyStateProps?.contacts ?? ([] as ContactDTO[]),
    )

    useEffect(() => {
        if (dynamicCreationHandler && locationInitCompanyStateProps) {
            setIsEditable(true)
        }
    }, [dynamicCreationHandler, locationInitCompanyStateProps])

    const {
        editing,

        setPhoto,
        setNewFiles,
        setRemovedFiles,

        onCompanySubmit,
        onPersonalSubmit,
    } = useFormConfig({
        isCompany,
        counterparty,
        refetch,
        setEditButtonText,
        setIsEditable,
        dynamicCreationHandler,
        defaultContacts,
    })

    useEffect(() => {
        if (counterparty) {
            setIsBlackList(counterparty?.isBlackList)
        }
    }, [counterparty])

    if (isLoading || editing) {
        return <Loader />
    }

    return (
        <>
            {!isCompany && (
                <Formik
                    innerRef={formikRef as any}
                    key={FormKeys.Personal}
                    initialValues={getInitialValuesPersonal(counterparty)}
                    validationSchema={ValidateSchemaPersonal}
                    onSubmit={onPersonalSubmit}
                    enableReinitialize
                >
                    {({ isValid }) => {
                        setDisabled(!isValid)

                        return (
                            <PersonalProfileForm
                                isEditable={isEditable}
                                counterparty={counterparty}
                                setPhoto={setPhoto}
                                setNewFiles={setNewFiles}
                                setRemovedFiles={setRemovedFiles}
                            />
                        )
                    }}
                </Formik>
            )}
            {isCompany && (
                <Formik
                    innerRef={formikRef as any}
                    key={FormKeys.Company}
                    initialValues={getInitialValuesCompany(
                        counterparty,
                        defaultContacts,
                    )}
                    validationSchema={ValidateSchemaOrg}
                    onSubmit={onCompanySubmit}
                    enableReinitialize
                >
                    {({ values, isValid }) => {
                        setDisabled(!isValid)

                        return (
                            <CompanyProfileForm
                                isEditable={isEditable}
                                counterparty={counterparty}
                                setPhoto={setPhoto}
                                contacts={values.contacts}
                                setDefaultContacts={setDefaultContacts}
                            />
                        )
                    }}
                </Formik>
            )}
        </>
    )
}
